import {create} from '@github-ui/dotcom-schema/protobuf'
import {
  ReviewerSchema as ProtoReviewerSchema,
  type Reviewer as ProtoReviewer,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/reviewer'

// Legacy response types retained for ergonomic test fixtures and endpoints
// that still feed legacy reviewer objects into shared proto-typed components.

type LegacyCopilotReviewEffort = 'low' | 'medium' | 'high'
type LegacyCopilotReviewEffortAvailability = 'available' | 'coming_soon'

type LegacyReviewerBase = {
  id: number
  displayLogin: string
  name: string | null
  avatarUrl: string
  // Populated only for bot reviewers (e.g. Copilot); null for users and teams. Used to source the
  // hovercard `botSlug` so it no longer depends on the soon-to-be-deprecated legacy display login.
  slug: string | null
}

export type LegacyUserReviewer = LegacyReviewerBase & {
  reviewerType: 'User'
  organizationLogin: null
}

export type LegacyTeamReviewer = LegacyReviewerBase & {
  reviewerType: 'Team'
  organizationLogin: string | null
}

export type LegacyReviewer = LegacyUserReviewer | LegacyTeamReviewer

type LegacyPendingReviewRequestBase = {
  reviewer: LegacyReviewer
  isCodeOwner: boolean
  isCodeOwnerFromDelegation: boolean
  delegatedFromTeam: LegacyTeamReviewer | null
  createdAt: string | null
  requestedCopilotReviewEffort?: LegacyCopilotReviewEffort | null
}

export type LegacyActivePendingReviewRequest = LegacyPendingReviewRequestBase & {deferredType: null}

export type LegacyDeferredPendingReviewRequest = LegacyPendingReviewRequestBase & {
  deferredType: 'codeowner' | 'copilot'
}

export type LegacyPendingReviewRequest = LegacyActivePendingReviewRequest | LegacyDeferredPendingReviewRequest

export type LegacyCompletedReview = {
  id: number
  canReRequest: boolean
  dismissedReviewState?: 'pending' | 'commented' | 'approved' | 'changes_requested' | 'dismissed' | null
  hasWriteAccess: boolean
  headSha: string
  onBehalfOfTeams: LegacyTeamReviewer[]
  reviewer: LegacyUserReviewer
  state: 'pending' | 'commented' | 'approved' | 'changes_requested' | 'dismissed'
  submittedAt: string
}

export type LegacyCopilotReviewEffortOption = {
  value: LegacyCopilotReviewEffort
  label: string
  description: string
  availability: LegacyCopilotReviewEffortAvailability
}

export type LegacyCopilotReviewerRow = {
  reviewer: LegacyUserReviewer
  requestable: boolean
  selectedReviewEffort: LegacyCopilotReviewEffort | null
  selectedReviewEffortSource?: string | null
  lastExecutedReviewEffort: LegacyCopilotReviewEffort | null
  reviewEffortOptions: LegacyCopilotReviewEffortOption[]
  learnMoreUrl: string | null
}

export type LegacyReviewRequestsData = {
  pendingReviewRequests: LegacyPendingReviewRequest[]
  completedReviews: LegacyCompletedReview[]
  copilotReviewerRow?: LegacyCopilotReviewerRow | null
  copilotReviewer?: LegacyUserReviewer | null
}

// Bridge review_summaries `onBehalfOfTeams` into shared proto-typed reviewer
// components until that endpoint migrates its embedded reviewer objects.
export function legacyReviewerToProto(reviewer: LegacyReviewer): ProtoReviewer {
  return create(ProtoReviewerSchema, {
    id: BigInt(reviewer.id),
    displayLogin: reviewer.displayLogin,
    name: reviewer.name ?? '',
    avatarUrl: reviewer.avatarUrl,
    reviewerType: reviewer.reviewerType,
    organizationLogin: reviewer.organizationLogin ?? '',
    slug: reviewer.slug ?? '',
  })
}
